// `ask_user` clarification parsing + answer building — the DATA half of the
// former `src/features/tool-approvals/renderers/ask-user-details.types.ts`
// (ARCFE-1059). Pure functions over the wire shapes; the inert types stay in
// `contracts/ask-user-details.types.ts`.
import {
  type AskUserKind,
  type AskUserOption,
  type ParsedAskUserInput,
  type ParsedClarificationQuestion,
  type ResolvedAnswer,
  SELF_FETCHING_PICKER_KINDS,
} from '../../contracts/ask-user-details.types';

/**
 * Internal UI sentinel for the "Other…" row. Deliberately distinct
 * from the wire-level ``"other"`` string the BE / agent sees in the
 * resolution payload — using ``"other"`` directly as the internal
 * Set / RadioGroup value would silently conflate with a legitimate
 * agent-supplied option whose id happens to be ``"other"`` (a fully
 * plausible LLM emission for an "Other / unspecified" choice).
 *
 * ``buildAnswer`` is the single seam where this internal value is
 * translated into the wire-level ``"other"`` for ``selected_option_ids``.
 */
export const OTHER_OPTION_ID = '__ask_user_other__';

/** Wire-level value emitted in ``selected_option_ids`` for the
 * ``allow_other`` choice. The agent reads this — keep it stable. */
export const OTHER_WIRE_VALUE = 'other';

const KNOWN_KINDS: readonly AskUserKind[] = [
  'choice',
  'text',
  'pick_session',
  'pick_patient',
  'pick_template',
  'pick_document',
  'pick_ehr',
  'pick_file',
  'connect_app',
  'login_computer',
];

/** Choice defaults Other on (omit = on); non-choice requires explicit true.
 * ``pick_file`` is a closed set — the BE rejects ``allow_other`` at request
 * validation and drops any submitted ``text``, so Other is never renderable. */
const resolveAllowOther = (kind: AskUserKind, rawAllowOther: unknown): boolean => {
  if (kind === 'pick_file') return false;
  if (kind === 'choice') {
    return rawAllowOther !== false;
  }
  return rawAllowOther === true;
};

const parseOptions = (rawOptions: unknown): AskUserOption[] => {
  if (!Array.isArray(rawOptions)) return [];
  return rawOptions
    .filter((o): o is Record<string, unknown> => typeof o === 'object' && o !== null)
    .map((o) => ({
      id: typeof o.id === 'string' ? o.id : '',
      label: typeof o.label === 'string' ? o.label : '',
      description: typeof o.description === 'string' ? o.description : undefined,
    }))
    .filter((o) => o.id && o.label);
};

/** Map agent kind (or shape) to a renderable AskUserKind. */
const resolveKind = (kindRaw: string, options: AskUserOption[]): AskUserKind | null => {
  if (KNOWN_KINDS.includes(kindRaw as AskUserKind)) {
    const kind = kindRaw as AskUserKind;
    // Closed option-set kinds with no options (today: pick_file) are
    // malformed — the BE rejects them before an approval exists. Degrade
    // to text so the wait stays answerable in chat. Empty choice and
    // self-fetching pickers keep their kind (empty is their expected shape).
    if (
      kind !== 'text' &&
      kind !== 'choice' &&
      // ``login_computer`` never carries options — the answer control is
      // the embedded live computer view plus Continue.
      kind !== 'login_computer' &&
      options.length === 0 &&
      !SELF_FETCHING_PICKER_KINDS.includes(kind)
    ) {
      return 'text';
    }
    return kind;
  }
  // Unknown kind with usable options → treat as choice. Unknown with
  // nothing to pick is unparseable — do not invent a free-text card.
  if (options.length > 0) return 'choice';
  return null;
};

export const parseInput = (
  raw: Record<string, unknown> | null | undefined
): ParsedAskUserInput | null => {
  if (!raw) return null;
  const question = typeof raw.question === 'string' ? raw.question.trim() : '';
  // The question is the one truly required field — it's the only thing
  // the user reads. Without it there's nothing to ask, so bail (the
  // caller renders a graceful free-text fallback).
  if (!question) return null;
  const kindRaw = typeof raw.kind === 'string' ? raw.kind : '';
  const options = parseOptions(raw.options);
  const kind = resolveKind(kindRaw, options);
  if (!kind) return null;
  const maxSelections =
    typeof raw.max_selections === 'number' && raw.max_selections >= 1
      ? Math.floor(raw.max_selections)
      : kind === 'connect_app'
        ? Math.max(options.length, 5)
        : 1;
  const allowOther = resolveAllowOther(kind, raw.allow_other);
  const placeholder = typeof raw.placeholder === 'string' ? raw.placeholder : '';
  const sessionId =
    typeof raw.session_id === 'string' && raw.session_id.trim()
      ? raw.session_id.trim()
      : undefined;
  const isCloudComputer =
    kind === 'login_computer'
      ? (raw.target as { is_cloud?: unknown } | null | undefined)?.is_cloud === true
      : undefined;
  return {
    question,
    kind,
    options,
    maxSelections,
    allowOther,
    placeholder,
    sessionId,
    isCloudComputer,
  };
};

/**
 * Decide whether the in-progress answer is ready to submit. Returning
 * ``null`` keeps the Send button disabled in the dialog (and the
 * inline submit handler falls through). The kinds vary widely enough
 * that a single boolean flag would obscure the intent — switch on
 * the kind explicitly so each rule reads top-down.
 */
export const buildAnswer = (
  parsed: ParsedAskUserInput,
  selectedIds: Set<string>,
  textValue: string,
  otherText: string
): ResolvedAnswer | null => {
  if (parsed.kind === 'text') {
    const trimmed = textValue.trim();
    if (!trimmed) return null;
    return { selected_option_ids: [], text: trimmed };
  }

  // ``login_computer`` — the user signs in through the embedded live view;
  // Continue is always submittable and the wire answer is a fixed
  // confirmation the agent reads as "logged in, carry on".
  if (parsed.kind === 'login_computer') {
    return { selected_option_ids: [], text: 'The user has signed in — continue.' };
  }

  // choice / picker kinds — require at least one selection.
  const ids = Array.from(selectedIds);
  if (ids.length === 0) return null;
  if (ids.length > parsed.maxSelections) return null;

  // ``allow_other`` is a choice-only modifier (the BE schema rejects
  // it for picker kinds), but we double-guard here so a corrupted
  // tool_input from a future BE version can't ship Send-able state
  // without the free-text leg the user expected.
  if (parsed.kind === 'choice' && parsed.allowOther && ids.includes(OTHER_OPTION_ID)) {
    const trimmedOther = otherText.trim();
    if (!trimmedOther) return null;
    // Translate the internal sentinel to the documented wire value
    // before it reaches the agent. Other ids pass through untouched
    // — only the "Other…" affordance gets remapped here.
    const wireIds = ids.map((id) => (id === OTHER_OPTION_ID ? OTHER_WIRE_VALUE : id));
    return { selected_option_ids: wireIds, text: trimmedOther };
  }

  return { selected_option_ids: ids, text: null };
};

/**
 * Synthetic answer-map key for a legacy single-shape clarification
 * (``{ question, kind, … }`` with no ``questions[]`` and thus no
 * agent-chosen id). Safe to invent: the BE constrains answer-map keys to
 * the agent's ids ONLY for batched (``questions[]``) calls — for a
 * single-shape ``tool_input`` ``_extract_asked_question_ids`` returns
 * ``None``, so whatever key we post at ``:resolve`` is echoed unconstrained.
 */
export const LEGACY_SINGLE_QUESTION_ID = 'answer';

/**
 * Parse one ``questions[]`` entry into a step, or ``null`` to drop it.
 * Drops entries with a missing/duplicate id (mutating ``seenIds``) or an
 * unparseable body.
 */
const parseQuestionEntry = (
  entry: unknown,
  seenIds: Set<string>
): ParsedClarificationQuestion | null => {
  if (typeof entry !== 'object' || entry === null) return null;
  const record = entry as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id.trim() : '';
  // Drop entries without a stable, unique id — it's the answer key, so a
  // missing/duplicate id would silently clobber another answer.
  if (!id || seenIds.has(id)) return null;
  const single = parseInput(record);
  if (!single) return null;
  seenIds.add(id);
  return { ...single, id };
};

/**
 * Parse a clarification ``tool_input`` into an ordered list of question
 * steps. The canonical shape is ``{ questions: [{ id, question, kind, … }] }``
 * (a single question is a one-element list). Entries without a usable,
 * unique id/question are dropped.
 *
 * A legacy single-shape input (``{ question, kind, … }`` with no
 * ``questions[]``) is normalized into a one-element list with a synthesized
 * id so the full card (pickers, choice) still renders instead of dropping
 * to the last-resort fallback.
 *
 * Returns ``null`` only when nothing renderable can be salvaged. Each
 * entry reuses ``parseInput`` so known kinds (including in-flight
 * ``text``) keep their original shape.
 */
export const parseQuestions = (
  raw: Record<string, unknown> | null | undefined
): ParsedClarificationQuestion[] | null => {
  if (!raw) return null;

  const rawQuestions = Array.isArray(raw.questions) ? raw.questions : null;
  if (!rawQuestions || rawQuestions.length === 0) {
    // Legacy single-shape input (no ``questions[]``). Normalize the
    // top-level ``{ question, kind, … }`` into a one-element list so the
    // full card renders rather than the featureless free-text fallback.
    const single = parseInput(raw);
    if (!single) return null;
    return [{ ...single, id: LEGACY_SINGLE_QUESTION_ID }];
  }

  const seenIds = new Set<string>();
  const parsed: ParsedClarificationQuestion[] = [];
  for (const entry of rawQuestions) {
    const step = parseQuestionEntry(entry, seenIds);
    if (step) parsed.push(step);
  }
  return parsed.length > 0 ? parsed : null;
};

/**
 * Split parsed questions into folded ``kind=text`` steps (answered in
 * chat) and structured card steps (choice / pickers). Other on choice
 * stays on the card — it is not ``kind=text``.
 */
export const partitionClarificationQuestions = (
  questions: readonly ParsedClarificationQuestion[]
): {
  text: ParsedClarificationQuestion[];
  structured: ParsedClarificationQuestion[];
} => ({
  text: questions.filter((question) => question.kind === 'text'),
  structured: questions.filter((question) => question.kind !== 'text'),
});

/**
 * Next folded text question with no draft entry yet. ``null`` (skip) and
 * a real answer both count as answered, so those ids are skipped here.
 */
export const nextUnansweredTextQuestion = (
  questions: readonly ParsedClarificationQuestion[],
  answersById: Record<string, ResolvedAnswer | null>
): ParsedClarificationQuestion | undefined =>
  questions.find((question) => question.kind === 'text' && !(question.id in answersById));

/**
 * Earliest unanswered question in the agent's own ``questions[]`` order,
 * whatever its kind. ``null`` (skip) and a real answer both count as
 * answered. This is the seam that decides which surface (folded chat text
 * vs. the structured card) owns the batch RIGHT NOW — see
 * ``isWaitingForFoldedTextClarification``.
 */
export const nextUnansweredQuestion = (
  questions: readonly ParsedClarificationQuestion[],
  answersById: Record<string, ResolvedAnswer | null>
): ParsedClarificationQuestion | undefined =>
  questions.find((question) => !(question.id in answersById));

/**
 * True when every parsed question has a draft entry (answer or skip).
 * An empty list is incomplete — there is nothing to resolve.
 */
export const clarificationAnswersComplete = (
  questions: readonly ParsedClarificationQuestion[],
  answersById: Record<string, ResolvedAnswer | null>
): boolean =>
  questions.length > 0 && questions.every((question) => question.id in answersById);

/**
 * A pending clarification the FE should fold into the transcript: every
 * parsed question is ``kind=text`` (one or many). The wait still opens;
 * sequential composer replies resolve it. Mixed batches hide the card
 * until every folded text question has a draft, then show structured
 * steps only.
 */
export const isFoldableTextClarification = (
  toolInput: Record<string, unknown> | null | undefined
): boolean => {
  const questions = parseQuestions(toolInput);
  return Boolean(
    questions?.length && questions.every((question) => question.kind === 'text')
  );
};

/**
 * True while a folded ``kind=text`` question still needs a composer
 * reply AND it is the EARLIEST unanswered question in the agent's own
 * order. The structured card stays hidden in that case so Continue
 * cannot look like the submit action for a wait that is still gated on
 * chat.
 *
 * Checking "is there an unanswered text question anywhere" (rather than
 * "is the very next one text") used to hide the whole card — including
 * an earlier, still-unanswered structured question — for as long as ANY
 * later text question in the batch remained unanswered. For a mixed
 * batch ordered ``[choice, text]`` that made the structured question
 * unreachable: the fold gate stayed true until the trailing text
 * question got a draft, but the only surface that could ever produce
 * that draft (the card) was the very thing being hidden. Gating on the
 * earliest unanswered question's kind respects the agent's own ordering
 * instead of always resolving every ``text`` question first.
 */
export const isWaitingForFoldedTextClarification = (
  toolInput: Record<string, unknown> | null | undefined,
  answersById: Record<string, ResolvedAnswer | null> = {}
): boolean => {
  const questions = parseQuestions(toolInput);
  if (!questions) return false;
  return nextUnansweredQuestion(questions, answersById)?.kind === 'text';
};

/**
 * Assemble the ``modified_input`` resolve payload from per-question
 * answers. One shape: ``{ answers: { <id>: … | null } }`` (``null`` = the
 * user skipped that question), keyed by the agent's question ids.
 */
export const buildClarificationModifiedInput = (
  questions: ParsedClarificationQuestion[],
  answersById: Record<string, ResolvedAnswer | null>
): Record<string, unknown> => {
  const answers: Record<string, ResolvedAnswer | null> = {};
  for (const question of questions) {
    answers[question.id] = answersById[question.id] ?? null;
  }
  return { answers };
};
